import { inject, Injectable } from "@angular/core";
import { isNil } from "lodash";
import { merge, Observable, Subject } from "rxjs";
import {
    filter,
    map,
    scan,
    share,
    shareReplay,
    switchMap,
    withLatestFrom
} from "rxjs/operators";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ILeverantor } from "~app/features/virkeskop/virkesleverantor/models/virkesleverantorModel";
import { UpdateKontraktBetalningarCommand } from "../commands/betalning/UpdateKontraktBetalningarCommand";
import { IVKKontraktBetalningDTO } from "../models/kontraktBetalningar-models";
import { AvtalService } from "./avtal.service";
import { KontraktCommandService } from "./kontrakt-command.service";
import { KontraktMediatorService } from "./kontrakt-mediator.service";
import { UtskriftMediatorService } from "./utskrift-mediator.service";

@Injectable()
export class BetalningMediatorService {
    readonly betalningar$: Observable<IVKKontraktBetalningDTO[]>;

    kontraktsbelopp$: Observable<number>;
    snittnetto$: Observable<number>;

    private readonly addBetalningSrc = new Subject<IVKKontraktBetalningDTO>();
    private readonly avtalService = inject(AvtalService);
    private readonly kontraktCommandService = inject(KontraktCommandService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly removeBetalningSrc = new Subject<IVKKontraktBetalningDTO>();
    private readonly updateResterandeSrc = new Subject<IVKKontraktBetalningDTO>();
    private readonly utskriftMediatorService = inject(UtskriftMediatorService);

    constructor() {
        this.snittnetto$ = this.kontraktMediatorService.vardeberakning$.pipe(
            map(berakning => berakning[0].sortimentSnittnetto.summa.totalt));

        this.kontraktsbelopp$ = this.utskriftMediatorService.kontraktsbeloppChange$;
        const reload$ =  this.kontraktMediatorService.kontrakt$
            .pipe(
                filter(kontrakt => !isNil(kontrakt.id)),
                switchMap(kontrakt => this.avtalService.getKontraktBetalningar(kontrakt.id)),
                shareReplay(1),
                map(betalningar => (_: IVKKontraktBetalningDTO[]) => betalningar));

        const updateResterande$ = this.updateResterandeSrc.pipe(
            map(resterande => (betalningar: IVKKontraktBetalningDTO[]) => {
                const index = betalningar.findIndex(b => b.virkesleverantorKontaktGuid === resterande.virkesleverantorKontaktGuid && b.utbetalningResterande);
                if(index > -1) {
                    return [
                        ...betalningar.slice(0,index),
                        resterande,
                        ...betalningar.slice(index + 1)
                    ];
                }
                return [
                    ...betalningar,
                    resterande
                ];
            }));

        const addBetalning$ = this.addBetalningSrc.pipe(
            share(),
            map(betalning => (betalningar: IVKKontraktBetalningDTO[]) => ([
                ...betalningar,
                betalning
            ]))
        );

        const removeBetalning$ = this.removeBetalningSrc.pipe(
            filter(betalning => !isNil(betalning)),
            share(),
            map(betalning => (betalningar: IVKKontraktBetalningDTO[]) => betalningar.filter(b => b.guid !== betalning.guid))
        );

        const updateCommand$ = merge(addBetalning$,removeBetalning$, updateResterande$).pipe(
            withLatestFrom(this.kontraktMediatorService.kontrakt$),
            map(([,kontrakt]) => (betalningar: IVKKontraktBetalningDTO[]) => {
                this.updateBetalningarCommand(kontrakt.guid, betalningar);
                return betalningar;
            })
        );

        this.betalningar$ = merge(reload$, addBetalning$, removeBetalning$, updateResterande$, updateCommand$).pipe(
            scan((
                acc: IVKKontraktBetalningDTO[],
                mutatorFn: (betalningar: IVKKontraktBetalningDTO[]) => IVKKontraktBetalningDTO[]) => mutatorFn(acc), [])
        );
    }

    addBetalning(betalning: IVKKontraktBetalningDTO) {
        this.addBetalningSrc.next(betalning);
    }

    calcAndelMarkagare(markagare: ILeverantor, snittnetto: number): number {
        if(ObjectUtils.isNullOrUndefined(markagare)) {
            return undefined;
        }
        if (markagare.andelTaljare && markagare.andelNamnare) {
            return Math.round(markagare.andelTaljare / markagare.andelNamnare * snittnetto);
        } else {
            return undefined;
        }
    }

    removeBetalning(betalning: IVKKontraktBetalningDTO) {
        this.removeBetalningSrc.next(betalning);
    }

    updateResterande(betalning: IVKKontraktBetalningDTO) {
        this.updateResterandeSrc.next(betalning);
    }

    private updateBetalningarCommand(kontraktGuid: string, betalningar: IVKKontraktBetalningDTO[]) {
        const command = new  UpdateKontraktBetalningarCommand(kontraktGuid, betalningar);
        this.kontraktCommandService.update(command);
    }
}
